import React, { useEffect, Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import { TopMenuBar } from '@/components/TopMenuBar';
import { Navigation } from '@/components/Navigation';
import { AppleHero } from '@/components/AppleHero';
import { HomeProductIntro } from '@/components/home/HomeProductIntro';
import { LazySection } from '@/components/home/LazySection';
import { LoadingSkeleton } from '@/components/ui/loading-skeleton';
import { unifiedPerformanceMonitor } from '@/utils/unifiedPerformanceMonitor';
import { useLocale } from '@/contexts/LocaleContext';
import { Footer } from '@/components/Footer';

// Lazy load below-the-fold heavy sections
const WebsiteBuildingDemo = lazy(() => import('@/components/home/WebsiteBuildingDemo').then(m => ({ default: m.WebsiteBuildingDemo })));
const WhyChooseUs = lazy(() => import('@/components/home/WhyChooseUs').then(m => ({ default: m.WhyChooseUs })));
const SoftwareIntelligenceSection = lazy(() => import('@/components/home/SoftwareIntelligenceSection'));
const InteractiveToolsShowcase = lazy(() => import('@/components/home/InteractiveToolsShowcase').then(m => ({ default: m.InteractiveToolsShowcase })));
const CenturionGuarantee = lazy(() => import('@/components/guarantee/CenturionGuarantee').then(m => ({ default: m.CenturionGuarantee })));
const TrustSocialProofSection = lazy(() => import('@/components/home/TrustSocialProofSection').then(m => ({ default: m.TrustSocialProofSection })));
const Pricing = lazy(() => import('@/components/Pricing').then(m => ({ default: m.Pricing })));
const HomeCTASection = lazy(() => import('@/components/home/HomeCTASection').then(m => ({ default: m.HomeCTASection })));
const RelatedLinksBlock = lazy(() => import('@/components/navigation/RelatedLinksBlock').then(m => ({ default: m.RelatedLinksBlock })));

// Section skeleton for consistent loading states
const SectionSkeleton = ({ height = 'h-64' }: { height?: string }) => (
  <div className={`w-full ${height} py-16`}>
    <div className="container mx-auto px-4">
      <LoadingSkeleton variant="rectangular" className="w-full h-32 mb-4" />
      <LoadingSkeleton variant="text" lines={2} className="max-w-xl mx-auto" />
    </div>
  </div>
);

const Index = () => {
  const location = useLocation();
  const { convertText } = useLocale();

  // Initialise unified performance monitoring
  useEffect(() => {
    unifiedPerformanceMonitor.initialize();
    
    return () => {
      unifiedPerformanceMonitor.cleanup();
    };
  }, []);

  // Scroll to top when navigating to homepage
  useEffect(() => {
    if (location.pathname === '/') {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
  }, [location.pathname]);

  return (
    <>
      {/* Fixed elements outside overflow container to prevent transform containment issues */}
      <TopMenuBar />
      <Navigation />
      
      <div className="min-h-screen bg-white relative overflow-x-hidden">
        <main className="relative z-10">
          {/* Above the fold - loaded immediately */}
          <AppleHero />
          <HomeProductIntro />
          
          {/* Below the fold - lazy loaded on scroll */}
          <LazySection expectedHeight="400px" minHeight="400px" rootMargin="300px">
            <Suspense fallback={<SectionSkeleton height="h-96" />}>
              <WebsiteBuildingDemo />
            </Suspense>
          </LazySection>

          <LazySection expectedHeight="600px" minHeight="300px" rootMargin="200px">
            <Suspense fallback={<SectionSkeleton height="h-64" />}>
              <WhyChooseUs />
            </Suspense>
          </LazySection>

          <LazySection expectedHeight="800px" minHeight="500px" rootMargin="200px">
            <Suspense fallback={<SectionSkeleton height="h-96" />}>
              <InteractiveToolsShowcase />
            </Suspense>
          </LazySection>

          <LazySection expectedHeight="400px" minHeight="300px" rootMargin="200px">
            <Suspense fallback={<SectionSkeleton height="h-64" />}>
              <TrustSocialProofSection />
            </Suspense>
          </LazySection>

          <LazySection expectedHeight="600px" minHeight="400px" rootMargin="200px">
            <Suspense fallback={<SectionSkeleton height="h-80" />}>
              <SoftwareIntelligenceSection />
            </Suspense>
          </LazySection>

          <LazySection expectedHeight="500px" minHeight="300px" rootMargin="200px">
            <Suspense fallback={<SectionSkeleton height="h-64" />}>
              <CenturionGuarantee />
            </Suspense>
          </LazySection>

          <LazySection expectedHeight="800px" minHeight="400px" rootMargin="200px">
            <div className="bg-gradient-to-b from-gray-50/50 to-white">
              <Suspense fallback={<SectionSkeleton height="h-80" />}>
                <Pricing />
              </Suspense>
            </div>
          </LazySection>

          <LazySection expectedHeight="300px" minHeight="200px" rootMargin="100px">
            <Suspense fallback={<SectionSkeleton height="h-48" />}>
              <HomeCTASection />
            </Suspense>
          </LazySection>

          <LazySection expectedHeight="150px" minHeight="100px" rootMargin="100px">
            <Suspense fallback={<SectionSkeleton height="h-32" />}>
              <RelatedLinksBlock />
            </Suspense>
          </LazySection>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Index;
